import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import { useRenameQueryResult } from "@/services/utils"
import { createQueryKey } from "@/services/utils/createQueryKey"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import type { QueryKey } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"

/** Org credentials list meta — not currency-scoped. */
export const credentialsListQueryKey = (
  organizationId: Organization["id"]
): QueryKey =>
  createQueryKey(ApiQueryName.Credentials, "getMany", null, {
    organizationId,
  })

export interface CredentialMeta {
  id: string
  notes?: string
  providerName: string
  resources: string[]
  username: string
  website?: string
}

/**
 * One row from GET .../pipelines/credentials after the httpClient
 * snake_case → camelCase response interceptor.
 */
interface CredentialApiRow {
  credentialId: string
  notes?: string
  resourceProviderId: string
  resourceTypeId: string
  username: string
  website?: string
}

const groupCredentialsById = (rows: CredentialApiRow[]): CredentialMeta[] => {
  const byId = new Map<string, CredentialMeta>()

  for (const row of rows) {
    const existing = byId.get(row.credentialId)
    if (existing) {
      if (
        row.resourceTypeId &&
        !existing.resources.includes(row.resourceTypeId)
      ) {
        existing.resources.push(row.resourceTypeId)
      }
      continue
    }

    byId.set(row.credentialId, {
      id: row.credentialId,
      notes: row.notes,
      providerName: row.resourceProviderId,
      resources: row.resourceTypeId ? [row.resourceTypeId] : [],
      username: row.username,
      website: row.website,
    })
  }

  return Array.from(byId.values()).map((credential) => ({
    ...credential,
    resources: [...credential.resources].sort((left, right) =>
      left.localeCompare(right)
    ),
  }))
}

const getCredentials = async (
  organizationId: Organization["id"]
): Promise<CredentialMeta[]> => {
  try {
    const response = await httpClient.get<CredentialApiRow[]>(
      `/api/v1/organizations/${organizationId}/pipelines/credentials`
    )

    return groupCredentialsById(response.data ?? [])
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseCredentialsOptions {
  organizationId: Organization["id"]
}

export const useCredentials = ({ organizationId }: UseCredentialsOptions) => {
  const queryResult = useQuery<CredentialMeta[], HttpError>({
    enabled: Boolean(organizationId),
    placeholderData: [],
    queryFn: async () => getCredentials(organizationId),
    queryKey: credentialsListQueryKey(organizationId),
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Credentials)
}
